/**
 * Calendars Service
 * API client for /v2/api/calendars endpoints
 */

import apiClient from '../client';
import type {
  Calendar,
  CalendarCreateRequest,
  CalendarUpdateRequest,
  Resource,
  ResourceCreateRequest,
  ResourceUpdateRequest,
  ResourceAvailabilityRequest,
  CalendarEvent,
  EventCreateRequest,
  EventUpdateRequest,
  CheckOverlapRequest,
  CheckOverlapResponse,
  UserOptionRequest,
  CalendarUserOption,
  ResourcesTableParams,
  ResourcesTableResponse,
  PaginationParams,
  ResourceListParams,
  SunTimesResponse,
  SunTimesParams,
  WeatherParams,
  WeatherForecastResponse,
  YearlyCalendarSummary,
} from '../types/calendars.types';

const BASE_PATH = '/calendars';

// ============================================================================
// CALENDAR MANAGEMENT
// ============================================================================

/**
 * Get all calendars accessible to the user
 */
export const getCalendars = async (
  params?: PaginationParams
): Promise<Calendar[]> => {
  const response = await apiClient.get<Calendar[]>(BASE_PATH, { params });
  return response.data;
};

/**
 * Get calendar by ID
 */
export const getCalendarById = async (
  calendarId: string
): Promise<Calendar> => {
  const response = await apiClient.get<Calendar>(
    `${BASE_PATH}/id/${calendarId}`
  );
  return response.data;
};

/**
 * Create a new calendar
 */
export const createCalendar = async (
  data: CalendarCreateRequest
): Promise<Calendar> => {
  const response = await apiClient.post<Calendar>(BASE_PATH, data);
  return response.data;
};

/**
 * Update an existing calendar
 */
export const updateCalendar = async (
  calendarId: string,
  data: CalendarUpdateRequest
): Promise<Calendar> => {
  const response = await apiClient.put<Calendar>(
    `${BASE_PATH}/id/${calendarId}`,
    data
  );
  return response.data;
};

/**
 * Delete (soft delete) a calendar
 */
export const deleteCalendar = async (calendarId: string): Promise<void> => {
  await apiClient.delete(`${BASE_PATH}/id/${calendarId}`);
};

// ============================================================================
// RESOURCE MANAGEMENT
// ============================================================================

/**
 * Get all resources with optional filtering
 */
export const getResources = async (
  params?: ResourceListParams
): Promise<Resource[]> => {
  const response = await apiClient.get<Resource[]>(`${BASE_PATH}/resources`, {
    params,
  });
  return response.data;
};

/**
 * Get resource by ID
 */
export const getResourceById = async (
  resourceId: string
): Promise<Resource> => {
  const response = await apiClient.get<Resource>(
    `${BASE_PATH}/resources/id/${resourceId}`
  );
  return response.data;
};

/**
 * Create a new resource
 */
export const createResource = async (
  data: ResourceCreateRequest
): Promise<Resource> => {
  const response = await apiClient.post<Resource>(
    `${BASE_PATH}/resources`,
    data
  );
  return response.data;
};

/**
 * Update a resource
 */
export const updateResource = async (
  resourceId: string,
  data: ResourceUpdateRequest
): Promise<Resource> => {
  const response = await apiClient.put<Resource>(
    `${BASE_PATH}/resources/id/${resourceId}`,
    data
  );
  return response.data;
};

/**
 * Toggle resource availability
 */
export const toggleResourceAvailability = async (
  resourceId: string,
  data: ResourceAvailabilityRequest
): Promise<Resource> => {
  const response = await apiClient.patch<Resource>(
    `${BASE_PATH}/resources/id/${resourceId}/availability`,
    data
  );
  return response.data;
};

/**
 * Delete (soft delete) a resource
 */
export const deleteResource = async (resourceId: string): Promise<void> => {
  await apiClient.delete(`${BASE_PATH}/resources/id/${resourceId}`);
};

// ============================================================================
// UDC TABLE - RESOURCES
// ============================================================================

/**
 * Get resources table with UDC format (pagination, filtering, sorting)
 */
export const getResourcesTable = async (
  params?: ResourcesTableParams
): Promise<ResourcesTableResponse> => {
  const response = await apiClient.get<ResourcesTableResponse>(
    `${BASE_PATH}/resources-table`,
    { params }
  );
  return response.data;
};

// ============================================================================
// EVENT MANAGEMENT
// ============================================================================

/**
 * Get events for a calendar
 */
export const getEvents = async (
  calendarId: string,
  params?: {
    start_date?: string;
    end_date?: string;
    resource_ids?: string[];
    skip?: number;
    limit?: number;
  }
): Promise<CalendarEvent[]> => {
  const response = await apiClient.get<CalendarEvent[]>(
    `${BASE_PATH}/id/${calendarId}/events`,
    { params }
  );
  return response.data;
};

/**
 * Get event by ID
 */
export const getEventById = async (eventId: string): Promise<CalendarEvent> => {
  const response = await apiClient.get<CalendarEvent>(
    `${BASE_PATH}/events/id/${eventId}`
  );
  return response.data;
};

/**
 * Create a new event with resource allocation
 */
export const createEvent = async (
  calendarId: string,
  data: EventCreateRequest
): Promise<CalendarEvent> => {
  const response = await apiClient.post<CalendarEvent>(
    `${BASE_PATH}/id/${calendarId}/events`,
    data
  );
  return response.data;
};

/**
 * Update an existing event
 */
export const updateEvent = async (
  eventId: string,
  data: EventUpdateRequest
): Promise<CalendarEvent> => {
  const response = await apiClient.put<CalendarEvent>(
    `${BASE_PATH}/events/id/${eventId}`,
    data
  );
  return response.data;
};

/**
 * Delete (soft delete) an event
 */
export const deleteEvent = async (eventId: string): Promise<void> => {
  await apiClient.delete(`${BASE_PATH}/events/id/${eventId}`);
};

// ============================================================================
// OVERLAP DETECTION
// ============================================================================

/**
 * Check for resource conflicts without creating an event
 */
export const checkOverlap = async (
  data: CheckOverlapRequest
): Promise<CheckOverlapResponse> => {
  const response = await apiClient.post<CheckOverlapResponse>(
    `${BASE_PATH}/events/check-overlap`,
    data
  );
  return response.data;
};

// ============================================================================
// USER OPTIONS
// ============================================================================

/**
 * Get user option by key
 */
export const getUserOption = async (
  key: string
): Promise<CalendarUserOption> => {
  const response = await apiClient.get<CalendarUserOption>(
    `${BASE_PATH}/options/${key}`
  );
  return response.data;
};

/**
 * Create or update user option
 */
export const setUserOption = async (
  data: UserOptionRequest
): Promise<CalendarUserOption> => {
  const response = await apiClient.post<CalendarUserOption>(
    `${BASE_PATH}/options`,
    data
  );
  return response.data;
};

// ============================================================================
// GOLDENWINGS - SUN TIMES (Sunrise/Sunset for airports)
// ============================================================================

/**
 * Get sun times (sunrise, sunset, twilight) for a specific airport
 * Supports both single date and date range queries
 * Used for VFR flight planning and daylight hours visualization
 *
 * @param airportId - Airport resource ID
 * @param params - Either { date } for single day or { date_from, date_to } for range
 * @returns SunTimesSingleDate for single date, SunTimesRange for date range
 */
export const getAirportSunTimes = async (
  airportId: string,
  params: SunTimesParams
): Promise<SunTimesResponse> => {
  const response = await apiClient.get<SunTimesResponse>(
    `/calendars/airports/${airportId}/sun-times`,
    { params }
  );
  return response.data;
};

// ============================================================================
// WEATHER FORECAST
// ============================================================================

/**
 * Get weather forecast for a location
 * @param params Weather query parameters (latitude, longitude, days, refresh)
 * @returns Weather forecast response with location and daily forecasts
 */
export const getWeatherForecast = async (
  params: WeatherParams
): Promise<WeatherForecastResponse> => {
  const response = await apiClient.get<WeatherForecastResponse>(
    `${BASE_PATH}/weather/forecast`,
    { params }
  );
  return response.data;
};

// ============================================================================
// HEALTH CHECKS
// ============================================================================

/**
 * Basic health check
 */
export const getHealth = async (): Promise<{
  status: string;
  timestamp: string;
}> => {
  const response = await apiClient.get<{ status: string; timestamp: string }>(
    `${BASE_PATH}/health`
  );
  return response.data;
};

/**
 * Readiness check (with database)
 */
export const getHealthReady = async (): Promise<{
  status: string;
  timestamp: string;
}> => {
  const response = await apiClient.get<{ status: string; timestamp: string }>(
    `${BASE_PATH}/health/ready`
  );
  return response.data;
};

// ============================================================================
// YEARLY SUMMARY
// ============================================================================

/**
 * Get yearly calendar summary (lightweight)
 * Returns daily aggregates instead of full events for performance
 *
 * @param calendarId - Calendar UUID
 * @param year - Year (2000-2100)
 * @param resourceIds - Optional array of resource UUIDs to filter by
 * @returns Yearly summary with day-by-day aggregates
 */
export const getYearlySummary = async (
  calendarId: string,
  year: number,
  resourceIds?: string[]
): Promise<YearlyCalendarSummary> => {
  const params: any = { year };

  if (resourceIds && resourceIds.length > 0) {
    params.resource_ids = resourceIds.join(',');
  }

  const response = await apiClient.get<YearlyCalendarSummary>(
    `${BASE_PATH}/id/${calendarId}/yearly-summary`,
    { params }
  );

  return response.data;
};

// ============================================================================
// EXPORTS
// ============================================================================

export const calendarsService = {
  // Calendars
  getCalendars,
  getCalendarById,
  createCalendar,
  updateCalendar,
  deleteCalendar,

  // Resources
  getResources,
  getResourceById,
  createResource,
  updateResource,
  toggleResourceAvailability,
  deleteResource,

  // UDC Table
  getResourcesTable,

  // Events
  getEvents,
  getEventById,
  createEvent,
  updateEvent,
  deleteEvent,

  // Overlap detection
  checkOverlap,

  // User options
  getUserOption,
  setUserOption,

  // GoldenWings - Sun times
  getAirportSunTimes,

  // GoldenWings - Weather
  getWeatherForecast,

  // Yearly summary (optimized for performance)
  getYearlySummary,

  // Health
  getHealth,
  getHealthReady,
};

export default calendarsService;
